/**
 * Umami analytics wrapper.
 *
 * The Umami script is loaded via a <script> tag in index.html and attaches a
 * global `window.umami`. It may be undefined (script blocked, ad-blocker, not
 * yet loaded), so every call is guarded — tracking must never break the app.
 *
 * - `track(event, data)` records a custom event.
 * - `identify(data)` attaches persistent session properties to the visitor,
 *   so reports can be segmented by role, company, plan, etc.
 *
 * Do NOT pass PII (emails, names, phone numbers) — EU/GDPR users. Use opaque
 * ids and categorical attributes only.
 */

type EventData = Record<string, string | number | boolean | undefined>

type Umami = {
  track: (event: string, data?: EventData) => void
  identify: (data: EventData) => void
}

declare global {
  interface Window {
    umami?: Umami
  }
}

/** Strip `undefined` values so Umami doesn't store empty props. */
function clean(data?: EventData): EventData | undefined {
  if (!data) return undefined
  const out: EventData = {}
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) out[key] = value
  }
  return Object.keys(out).length > 0 ? out : undefined
}

export function track(event: string, data?: EventData): void {
  try {
    window.umami?.track(event, clean(data))
  } catch {
    // analytics must never throw into the UI
  }
}

export function identify(data: EventData): void {
  try {
    const cleaned = clean(data)
    if (cleaned) window.umami?.identify(cleaned)
  } catch {
    // analytics must never throw into the UI
  }
}

/** Centralized event names — avoids typos and documents what we track. */
export const ANALYTICS_EVENTS = {
  // auth
  signinSuccess: 'signin_success',
  signinFailure: 'signin_failure',
  signupCarrierSuccess: 'signup_carrier_success',
  signupCarrierStep: 'signup_carrier_step',
  // onboarding / company
  documentUploaded: 'document_uploaded',
  vatLookupRun: 'vat_lookup_run',
  companyInfoUpdated: 'company_info_updated',
  verificationSubmitted: 'verification_submitted',
  profileUpdated: 'profile_updated',
  // loads
  loadPosted: 'load_posted',
  loadUpdated: 'load_updated',
  loadDeleted: 'load_deleted',
  loadDetailViewed: 'load_detail_viewed',
  loadMarkedDelivered: 'load_marked_delivered',
  loadReposted: 'load_reposted',
  loadsFiltered: 'loads_filtered',
  // bids
  bidPlaced: 'bid_placed',
  bidAccepted: 'bid_accepted',
  bidRejected: 'bid_rejected',
  bidWithdrawn: 'bid_withdrawn',
  // fleet
  vehicleCreated: 'vehicle_created',
  vehicleUpdated: 'vehicle_updated',
  vehicleDeleted: 'vehicle_deleted',
  // routes
  routeCreated: 'route_created',
  routeUpdated: 'route_updated',
  routeDeleted: 'route_deleted',
  // matching
  matchClicked: 'match_clicked',
} as const

/**
 * Bucket a numeric age (days) into a small, low-cardinality label so Umami
 * doesn't see a unique value per visitor. Buckets follow standard SaaS cohorts.
 */
export function ageDaysBucket(days: number | null | undefined): string | undefined {
  if (days == null || Number.isNaN(days) || days < 0) return undefined
  if (days < 1) return 'd0'
  if (days < 7) return 'd1_6'
  if (days < 30) return 'd7_29'
  if (days < 90) return 'd30_89'
  if (days < 365) return 'd90_364'
  return 'd365_plus'
}

/** Bucket a numeric value (e.g. weight tons, price) for low-cardinality props. */
export function numberBucket(
  value: number | null | undefined,
  edges: number[],
): string | undefined {
  if (value == null || Number.isNaN(value)) return undefined
  for (let i = 0; i < edges.length; i++) {
    if (value < edges[i]) return i === 0 ? `lt_${edges[0]}` : `${edges[i - 1]}_${edges[i]}`
  }
  return `${edges[edges.length - 1]}_plus`
}
